/**
 * `/festschreibung` — CompanyAdmin month locks for their company projects (PRD §3.2, §7.2).
 * Mirrors Admin → Festschreibung without the cross-tenant company picker; unlock is G-Admin only.
 */

import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { MonthLockPanel } from "@/components/tracking/month-lock-panel";
import { useQueryParam, useQueryYear } from "@/lib/query-param";
import { filterArchived, useProjects } from "@/lib/tenancy";
import { berlinToday } from "@/shared/dates";
import { projectYears } from "@/shared/years";

export function FestschreibungPage() {
  const { t } = useTranslation();
  const projects = useProjects({ archived: false });
  const today = berlinToday();
  const todayYear = Number(today.slice(0, 4));
  const [projectId, setProjectId] = useQueryParam("project", { fallback: "" });

  const companyProjects = filterArchived(projects.data ?? [], false);
  const project = companyProjects.find((row) => row.id === projectId);
  const years = project ? projectYears(project.startDate, project.endDate, todayYear) : [];
  const [year, setYear] = useQueryYear("year", years, todayYear);

  if (projects.isLoading) {
    return <Skeleton className="h-40 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">
          {t("festschreibung.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("festschreibung.body")}</p>
      </div>

      <div className="max-w-md space-y-1.5">
        <Label>{t("festschreibung.project")}</Label>
        <Select value={projectId || undefined} onValueChange={setProjectId}>
          <SelectTrigger aria-label={t("festschreibung.project")}>
            <SelectValue placeholder={t("festschreibung.pickProject")} />
          </SelectTrigger>
          <SelectContent>
            {companyProjects.map((row) => (
              <SelectItem key={row.id} value={row.id}>
                {row.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!project && (
        <p className="text-sm text-muted-foreground">{t("festschreibung.pickProjectHint")}</p>
      )}
      {project && (
        <MonthLockPanel
          projectId={project.id}
          year={year}
          onYearChange={setYear}
          yearEditable
          projectStartDate={project.startDate}
          projectEndDate={project.endDate}
          history
          canUnlock={false}
        />
      )}
    </div>
  );
}
